import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Bug, ClipboardCopy, ExternalLink, RotateCcw } from 'lucide-react';
import { Botao, Segmentado, Selo, type CorSelo } from '../../componentes/base';
import { copiarTexto } from '../../componentes/copiar';
import { Estado, Vazio } from '../../componentes/estados';
import { AreaSensivel } from '../../componentes/formulario';
import { Drawer } from '../../componentes/sobreposicao';
import { Tabela, type Coluna } from '../../componentes/tabela';
import { useToast } from '../../componentes/toast';
import { get, mensagemDeErro, patch } from '../../nucleo/api';
import { censurarTexto, dataHora, nomePessoa, textoLivre } from '../../nucleo/formato';
import i18n from 'i18next';
import { rotaDoLog } from '@malgoos/shared';
import { discretoAtivo, nomesOcultos } from '../../nucleo/discreto';
import { assinarErrosRegistrados, ehTextoFixo, podeRefazer, refazerErro, registrarErro } from '../../nucleo/log-erros';

/**
 * CRM no novo desenho, lote 0 (ficha `demandas/entrada/2026-10-26_malgo_MOD_crm-novo-desenho.md`): Admin > Logs de erros.
 * O que a pessoa tentava fazer quando a tela deu erro, para a equipe (ou uma IA de apoio) achar, corrigir e refazer a ação
 * antes de a pessoa precisar avisar. Filtro por situação, "Copiar relato para a IA", "Executar de novo" (na mesma aba em que
 * o erro aconteceu; fora dela, "Abrir a tela"), notas da equipe e "Simular erro" para testar.
 */
type Status = 'novo' | 'analise' | 'corrigido';
type Filtro = 'abertos' | Status | 'todos';
const FILTROS: readonly Filtro[] = ['abertos', 'novo', 'analise', 'corrigido', 'todos'];
interface LinhaErro {
  id: string;
  /** Ajustes 27/10 (decisão 5): o "Dê seu feedback" das telas entra no mesmo log. */
  tipo?: 'erro' | 'feedback';
  origem: string;
  acao: string;
  rota: string;
  tela: string | null;
  erro: string;
  request_id: string | null;
  status: Status;
  created_at: string;
  usuario_nome: string | null;
}
interface DetalheErro extends LinhaErro {
  /** Nulo para quem não vê a empresa ou o módulo da tela (rodada crm1, SEG-M1). */
  dados: Record<string, string | boolean | number> | null;
  dados_ocultos?: boolean;
  pilha: string | null;
  navegador: string | null;
  notas: { em: string; por: string; texto: string }[];
}

const COR: Record<Status, CorSelo> = {
  novo: 'perigo',
  analise: 'dourado',
  corrigido: 'verde',
};
const CLASSE_AREA = 'w-full min-w-0 resize-y rounded-lg border border-borda-campo bg-campo px-3 py-2 text-sm text-tinta-1 focus:border-foco focus:outline-2 focus:outline-offset-0 focus:outline-foco';

/**
 * O relato pronto para colar numa IA: o que a pessoa fazia, o erro, a pilha e o que estava preenchido. Com o modo
 * discreto ligado, o relato sai pelo mesmo formatador de texto livre da tela (lista de 02/11); os rótulos seguem o idioma da tela (rodada crm1, SPEC-B5).
 */
/**
 * Review rcrm3 visual (M2): a ação do feedback é gravada em pt pelo servidor ("Dê seu feedback"); a tela a mostra no idioma da
 * conta. A do erro é o texto que a própria tela mandou.
 */
/**
 * Correções de 02/11 (rcorr-1): tabela, gaveta e "Copiar" usam estas mesmas funções, inclusive para os registros já gravados
 * (nenhum é alterado). `acao` e `tela` com a opção de nomes do discreto só aparecem se forem texto fixo da interface, sem adivinhar
 * nome; rota antiga aparece sem a busca. Em `erro`, `pilha` e `dados`, que são texto livre de verdade, palavras capitalizadas em
 * seguida (com ou sem "de", "da"...) contam como nome e viram iniciais, e o título do registro não aparece no discreto.
 */
const NOME_PROPRIO = /\p{Lu}[\p{Ll}'’-]+(?:\s+(?:(?:d[aeo]s?|e)\s+)?\p{Lu}[\p{Ll}'’-]+)+/gu;
export const livreDoLog = (texto: string | null | undefined): string => textoLivre(texto, ...(texto?.match(NOME_PROPRIO) ?? []));

type Traduzir = (chave: string) => string;
const tradutorAdmin: Traduzir = (c) => i18n.t(c, { ns: 'admin' });

/** Textos fixos que a web grava em pt (o genérico do controle e o que diz de onde veio o erro); a leitura os mostra no idioma da conta. */
const ACAO_GRAVADA: Record<string, string> = {
  Botão: 'botao',
  Link: 'link',
  Opção: 'opcao',
  Seção: 'secao',
  Campo: 'campo',
  'Carregar a tela': 'carregarTela',
  'Fora de um clique': 'foraDeClique',
  'Simular erro (teste)': 'simularErro',
};
function acaoLegivel(acao: string, t: Traduzir): string {
  const gravada = ACAO_GRAVADA[acao];
  if (gravada) return t(`erros.gravado.${gravada}`);
  const render = /^Abrir a tela(?: \((.+)\))?$/.exec(acao);
  if (render) return render[1] ? `${t('erros.gravado.abrirTela')} (${acaoLegivel(render[1], t)})` : t('erros.gravado.abrirTela');
  return nomesOcultos() && !ehTextoFixo(acao) ? t('erros.gravado.botao') : censurarTexto(acao);
}

export const acaoDoLog = (l: Pick<DetalheErro, 'tipo' | 'acao'>, t: Traduzir = tradutorAdmin) => (l.tipo === 'feedback' ? t('erros.acaoFeedback') : acaoLegivel(l.acao, t));

const SUFIXO_JANELA = ' (janela)';
/** Rota gravada antes da regra nova ainda pode ter `?busca=`: a leitura a mostra como a gravação de hoje. */
const rotaLegivel = (rota: string) => rotaDoLog(rota, censurarTexto);
export function telaDoLog(l: Pick<DetalheErro, 'rota' | 'tela'>, t: Traduzir = tradutorAdmin): string {
  const rota = rotaLegivel(l.rota);
  if (!l.tela) return rota;
  const comJanela = l.tela.endsWith(SUFIXO_JANELA);
  const tela = comJanela ? l.tela.slice(0, -SUFIXO_JANELA.length) : l.tela;
  if (nomesOcultos() && !tela.startsWith('/') && !ehTextoFixo(tela)) return rota;
  return `${censurarTexto(tela)}${comJanela ? ` (${t('erros.gravado.janela')})` : ''} · ${rota}`;
}

/** "O que estava preenchido" de registro antigo pode ter o título da negociação: no discreto, fica de fora. */
const CHAVE_DE_TITULO = /t[ií]tulo|title/i;
export const dadosDoLog = (dados: NonNullable<DetalheErro['dados']>) => (discretoAtivo() ? Object.fromEntries(Object.entries(dados).filter(([k]) => !CHAVE_DE_TITULO.test(k))) : dados);

export function relatoParaIA(l: DetalheErro, t: Traduzir = tradutorAdmin): string {
  const r = (c: string) => t(`erros.relato.${c}`);
  return [
    r('intro'),
    '',
    `${r('quando')}: ${dataHora(l.created_at)}`,
    `${r('pessoa')}: ${nomePessoa(l.usuario_nome)}`,
    `${r('acao')}: ${acaoDoLog(l, t)}`,
    `${r('tela')}: ${telaDoLog(l, t)}`,
    `${r('origem')}: ${l.origem}`,
    `${r('erro')}: ${livreDoLog(l.erro)}`,
    l.request_id ? `${r('requestId')}: ${l.request_id}` : '',
    `${r('pilha')}:`,
    l.pilha ? livreDoLog(l.pilha) : r('semPilha'),
    `${r('dados')}:`,
    l.dados ? livreDoLog(JSON.stringify(dadosDoLog(l.dados), null, 2)) : r('dadosOcultos'),
    `${r('navegador')}: ${l.navegador ?? ''}`,
  ]
    .filter((x) => x !== '')
    .join('\n');
}

export function LogsErros() {
  const { t } = useTranslation('admin');
  const [url, setUrl] = useSearchParams();
  const [filtro, setFiltro] = useState<Filtro>('abertos');
  const aberto = url.get('id');
  const qc = useQueryClient();
  // Erro novo registrado nesta aba (inclusive o simulado): a lista e as contagens recarregam.
  useEffect(() => assinarErrosRegistrados(() => void qc.invalidateQueries({ queryKey: ['/erros'] })), [qc]);
  const contagem = useQuery({
    queryKey: ['/erros', 'contagem'],
    queryFn: () =>
      get<{ contagem: Partial<Record<Status, number>> }>('/erros', {
        limite: 1,
        status: 'todos',
      }),
  });
  const c = contagem.data?.contagem ?? {};
  const n = (f: Filtro) => (f === 'todos' ? (c.novo ?? 0) + (c.analise ?? 0) + (c.corrigido ?? 0) : f === 'abertos' ? (c.novo ?? 0) + (c.analise ?? 0) : (c[f] ?? 0));
  const abrir = (id: string | null) => {
    const prox = new URLSearchParams(url);
    if (id) prox.set('id', id);
    else prox.delete('id');
    setUrl(prox, { replace: !id });
  };

  const colunas: Coluna<LinhaErro>[] = [
    {
      chave: 'created_at',
      rotulo: t('erros.quando'),
      mono: true,
      render: (l) => dataHora(l.created_at),
    },
    {
      chave: 'usuario_nome',
      rotulo: t('erros.pessoa'),
      render: (l) => nomePessoa(l.usuario_nome),
    },
    {
      chave: 'acao',
      rotulo: t('erros.acao'),
      principal: true,
      render: (l) => (
        <span className="flex flex-wrap items-center gap-1.5 font-semibold">
          {l.tipo === 'feedback' && <Selo cor="ciano">{t('erros.feedback')}</Selo>}
          {acaoDoLog(l, t)}
        </span>
      ),
    },
    {
      chave: 'rota',
      rotulo: t('erros.tela'),
      render: (l) => <span className="text-xs break-all text-tinta-2">{telaDoLog(l, t)}</span>,
    },
    {
      chave: 'erro',
      rotulo: t('erros.erro'),
      render: (l) => <span className="text-xs break-words">{livreDoLog(l.erro).slice(0, 90)}</span>,
    },
    {
      chave: 'status',
      rotulo: t('erros.situacao'),
      render: (l) => <Selo cor={COR[l.status]}>{t(`erros.status.${l.status}`)}</Selo>,
    },
  ];

  return (
    <div className="flex flex-col gap-4" data-logs-erros="">
      <p className="max-w-3xl text-sm text-tinta-2">{t('erros.intro')}</p>
      <Tabela<LinhaErro>
        tela="admin.erros"
        caminho="/erros"
        params={{ status: filtro }}
        chaveConsulta={['/erros', filtro]}
        filtros={{ status: filtro }}
        aoAplicarFiltros={(f) => setFiltro(FILTROS.includes(f.status as Filtro) ? (f.status as Filtro) : 'abertos')}
        colunas={colunas}
        aoClicar={(l) => abrir(l.id)}
        vazio={<Vazio icone={<Bug className="size-5" aria-hidden />} titulo={t(filtro === 'todos' ? 'erros.vazio' : 'erros.vazioFiltro')} texto={t('erros.vazioTexto')} />}
        filtrosExtra={
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <Segmentado<Filtro>
              rotulo={t('erros.situacao')}
              valor={filtro}
              aoMudar={setFiltro}
              data-filtro-erros=""
              opcoes={FILTROS.map((f) => ({
                valor: f,
                rotulo: `${t(`erros.filtros.${f}`)} (${n(f)})`,
              }))}
            />
            <Botao
              variante="secundario"
              tamanho="sm"
              icone={<Bug className="size-4" />}
              data-simular-erro=""
              onClick={() =>
                void registrarErro(new Error(t('erros.erroDeTeste')), {
                  origem: 'simulado',
                })
              }
            >
              {t('erros.simular')}
            </Botao>
          </div>
        }
      />
      {aberto && <Detalhe id={aberto} aoFechar={() => abrir(null)} />}
    </div>
  );
}

function Detalhe({ id, aoFechar }: { id: string; aoFechar: () => void }) {
  const { t } = useTranslation('admin');
  const { t: tn } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const navegar = useNavigate();
  const [nota, setNota] = useState('');
  const q = useQuery({
    queryKey: ['/erros', 'detalhe', id],
    queryFn: () => get<DetalheErro>(`/erros/${id}`),
  });
  const mudar = useMutation({
    mutationFn: (status?: Status) => patch(`/erros/${id}`, { status, nota: nota.trim() || undefined }),
    onSuccess: (_r, status) => {
      setNota('');
      void qc.invalidateQueries({ queryKey: ['/erros'] });
      toast.sucesso(status ? t(`erros.mudou.${status}`) : t('erros.notaSalva'));
      // Como no clone: mudar a situação fecha a janela; só a nota a mantém aberta.
      if (status) aoFechar();
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const refazivel = podeRefazer(id);
  const executar = () => {
    const p = refazerErro(id);
    if (!p) return;
    aoFechar();
    p.then(
      () => toast.sucesso(t('erros.executou')),
      (e: unknown) => toast.erro(mensagemDeErro(e)),
    );
  };
  return (
    <Drawer
      aberto
      aoFechar={aoFechar}
      largura="lg"
      titulo={t('erros.detalhe')}
      rodape={
        q.data && (
          <div className="flex flex-wrap justify-end gap-2">
            <Botao
              variante="secundario"
              icone={<ClipboardCopy className="size-4" />}
              onClick={async () => ((await copiarTexto(relatoParaIA(q.data, t))) ? toast.sucesso(t('erros.copiado')) : toast.erro(t('erros.naoCopiou')))}
            >
              {t('erros.copiarRelato')}
            </Botao>
            {refazivel ? (
              <Botao variante="secundario" icone={<RotateCcw className="size-4" />} onClick={executar} data-executar-de-novo="">
                {t('erros.executarDeNovo')}
              </Botao>
            ) : (
              <Botao variante="secundario" icone={<ExternalLink className="size-4" />} onClick={() => navegar(rotaDoLog(q.data.rota))}>
                {t('erros.abrirTela')}
              </Botao>
            )}
            {q.data.status !== 'analise' && q.data.status !== 'corrigido' && (
              <Botao variante="secundario" carregando={mudar.isPending && mudar.variables === 'analise'} onClick={() => mudar.mutate('analise')}>
                {t('erros.emAnalise')}
              </Botao>
            )}
            {q.data.status !== 'corrigido' ? (
              <Botao variante="primario" carregando={mudar.isPending && mudar.variables === 'corrigido'} onClick={() => mudar.mutate('corrigido')}>
                {t('erros.marcarCorrigido')}
              </Botao>
            ) : (
              <Botao variante="secundario" carregando={mudar.isPending && mudar.variables === 'novo'} onClick={() => mudar.mutate('novo')}>
                {t('erros.reabrir')}
              </Botao>
            )}
          </div>
        )
      }
    >
      <Estado consulta={q}>
        {(l) => {
          const dados = Object.entries(dadosDoLog(l.dados ?? {}));
          return (
            <div className="flex flex-col gap-5 text-sm" data-erro-detalhe={l.id}>
              <p className="flex flex-wrap items-center gap-2">
                <Selo cor={COR[l.status]}>{t(`erros.status.${l.status}`)}</Selo>
                <span className="text-tinta-3">
                  {dataHora(l.created_at)} · {nomePessoa(l.usuario_nome)}
                </span>
              </p>
              <section>
                <h3 className="kicker mb-1">{t('erros.acaoTentava')}</h3>
                <p className="font-semibold text-tinta-1">{acaoDoLog(l, t)}</p>
                <p className="text-xs break-all text-tinta-3">{telaDoLog(l, t)}</p>
              </section>
              <section data-tipo-log={l.tipo ?? 'erro'}>
                <h3 className="kicker mb-1">{l.tipo === 'feedback' ? t('erros.textoFeedback') : t('erros.erro')}</h3>
                <p className={`break-words ${l.tipo === 'feedback' ? 'text-tinta-1' : 'text-perigo'}`}>{livreDoLog(l.erro)}</p>
                {l.request_id && <p className="numero mt-1 text-xs text-tinta-3">request_id: {l.request_id}</p>}
                {l.pilha && <pre className="numero mt-2 max-h-48 overflow-auto rounded-md bg-sup-2 p-2 text-xs whitespace-pre-wrap">{livreDoLog(l.pilha)}</pre>}
              </section>
              <section>
                <h3 className="kicker mb-1">{t('erros.preenchido')}</h3>
                {l.dados === null ? (
                  <p className="text-tinta-3" data-dados-ocultos="">
                    {t('erros.dadosOcultos')}
                  </p>
                ) : dados.length ? (
                  <dl className="grid grid-cols-3 gap-x-3 gap-y-1 rounded-lg border border-borda p-3">
                    {dados.map(([k, v]) => (
                      <div key={k} className="contents">
                        <dt className="numero truncate text-xs text-tinta-3" title={k}>
                          {k}
                        </dt>
                        <dd className="col-span-2 break-words">{typeof v === 'string' ? livreDoLog(v) : String(v)}</dd>
                      </div>
                    ))}
                  </dl>
                ) : (
                  <p className="text-tinta-3">{t('erros.nadaPreenchido')}</p>
                )}
              </section>
              {l.navegador && (
                <section>
                  <h3 className="kicker mb-1">{t('erros.navegador')}</h3>
                  <p className="text-xs break-all text-tinta-2">{l.navegador}</p>
                </section>
              )}
              <section>
                <h3 className="kicker mb-1">{t('erros.notas')}</h3>
                <ul className="mb-2 flex flex-col gap-2">
                  {l.notas.map((x, i) => (
                    <li key={i} className="rounded-lg border border-borda p-2">
                      <span className="block text-xs text-tinta-3">
                        {dataHora(x.em)} · {nomePessoa(x.por)}
                      </span>
                      <span className="break-words whitespace-pre-wrap">{livreDoLog(x.texto)}</span>
                    </li>
                  ))}
                </ul>
                <AreaSensivel id="erro-nota" rotulo={t('erros.notas')} aria-label={t('erros.notas')} rows={2} maxLength={1000} className={CLASSE_AREA} placeholder={t('erros.notaPlaceholder')} value={nota} onChange={(e) => setNota(e.target.value)} />
                {nota.trim() && (
                  <div className="mt-2 flex justify-end">
                    <Botao variante="fantasma" tamanho="sm" carregando={mudar.isPending && mudar.variables === undefined} onClick={() => mudar.mutate(undefined)}>
                      {tn('comum.salvar')}
                    </Botao>
                  </div>
                )}
              </section>
              {!refazivel && <p className="text-xs text-tinta-3">{t('erros.soNaMesmaAba')}</p>}
            </div>
          );
        }}
      </Estado>
    </Drawer>
  );
}
